/**
 * Merge product-level seller fields with seller-page scrape rows and dedupe by identity.
 */

export function normalizeStoreUrl(input?: string): string {
  if (!input || typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  try {
    const u = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    const path = (u.pathname || '/').replace(/\/+$/, '') || '/';
    return `${u.hostname.toLowerCase()}${path}`;
  } catch {
    return trimmed.toLowerCase();
  }
}

export function sellerKey(s: any): string {
  try {
    const name = String(s?.name || s?.seller || s?.seller_name || '')
      .trim()
      .toLowerCase();
    const href = String(s?.store_url || s?.seller_store_url || s?.url || '').trim();
    if (href) {
      const store = normalizeStoreUrl(href);
      if (store) return `url:${store}`;
    }
    if (name) return `name:${name}`;
    return `raw:${JSON.stringify(s)}`;
  } catch {
    return 'unknown';
  }
}

function sellerNameKey(s: any): string {
  return String(s?.name || s?.seller || s?.seller_name || '')
    .trim()
    .toLowerCase();
}

function sellerStoreKey(s: any): string {
  return normalizeStoreUrl(s?.store_url || s?.seller_store_url || s?.url);
}

function pickSellerName(...records: any[]): string | undefined {
  for (const r of records) {
    const n = r?.name || r?.seller || r?.seller_name;
    if (typeof n === 'string' && n.trim()) return n.trim();
  }
  return undefined;
}

/** Merge two seller records; prefer non-empty store URL and crawl metadata. */
function mergeSellerRecords(a: any, b: any): any {
  const name = pickSellerName(a, b);
  const storeUrl =
    a?.store_url ||
    a?.seller_store_url ||
    b?.store_url ||
    b?.seller_store_url;
  const storeNorm = normalizeStoreUrl(storeUrl);
  const pageUrl =
    [a?.url, b?.url].find(u => u && normalizeStoreUrl(String(u)) === storeNorm) ||
    a?.url ||
    b?.url ||
    storeUrl;

  return {
    ...b,
    ...a,
    ...(name ? { name, seller: name, seller_name: name } : {}),
    store_url: storeUrl,
    seller_store_url: storeUrl,
    url: pageUrl,
    rating: a?.rating || a?.seller_rating || b?.rating || b?.seller_rating,
    description: a?.description || b?.description,
    location: a?.location || b?.location,
    contact: a?.contact || b?.contact,
    screenshot_url: a?.screenshot_url || b?.screenshot_url,
  };
}

function sellersMatch(a: any, b: any): boolean {
  const nameA = sellerNameKey(a);
  const nameB = sellerNameKey(b);
  if (nameA && nameB && nameA === nameB) return true;

  const storeA = sellerStoreKey(a);
  const storeB = sellerStoreKey(b);
  if (storeA && storeB && storeA === storeB) return true;

  return false;
}

export function dedupeSellers(arr: any[]): any[] {
  const out: any[] = [];
  for (const s of arr || []) {
    const idx = out.findIndex(ex => sellersMatch(ex, s));
    if (idx >= 0) {
      out[idx] = mergeSellerRecords(out[idx], s);
    } else {
      out.push(s);
    }
  }
  return out;
}

function sellersFromProduct(p: any): any[] {
  const out: any[] = [];
  if (!p) return out;
  if (Array.isArray(p.sellers)) {
    out.push(...p.sellers);
  }
  const name = p.seller || p.seller_name;
  if (typeof name === 'string' && name.trim() !== '') {
    const trimmed = name.trim();
    out.push({
      name: trimmed,
      seller: trimmed,
      seller_name: trimmed,
      store_url: p.seller_store_url || p.seller_store_url,
      url: p.seller_store_url || p.seller_store_url,
      rating: p.seller_rating,
      description: p.seller_description,
    });
  }
  return out;
}

/**
 * Collect sellers for a listing: discovered seller-page rows plus product/group seller fields.
 * Fills missing names on store-only rows from product seller name when store URLs match.
 */
export function collectListingSellers(options: {
  product: any;
  discovered?: any[];
  groupProducts?: Array<{ product: any }>;
}): any[] {
  const { product, discovered = [], groupProducts = [] } = options;
  const collected: any[] = [...discovered];

  collected.push(...sellersFromProduct(product));
  for (const { product: p } of groupProducts) {
    collected.push(...sellersFromProduct(p));
  }

  const nameByStore = new Map<string, string>();
  const addNameMapping = (name: string, storeRaw?: string) => {
    const store = normalizeStoreUrl(storeRaw);
    if (name && store) nameByStore.set(store, name);
  };

  for (const { product: p } of [product, ...groupProducts.map(g => g.product)]) {
    const name = p?.seller || p?.seller_name;
    if (typeof name === 'string' && name.trim()) {
      addNameMapping(name.trim(), p?.seller_store_url);
    }
  }

  for (const s of collected) {
    const name = s?.name || s?.seller || s?.seller_name;
    if (typeof name === 'string' && name.trim()) {
      addNameMapping(
        name.trim(),
        s.store_url || s.seller_store_url || s.url
      );
    }
  }

  const defaultName =
    typeof product?.seller === 'string' && product.seller.trim()
      ? product.seller.trim()
      : typeof product?.seller_name === 'string' && product.seller_name.trim()
        ? product.seller_name.trim()
        : undefined;

  const enriched = collected.map(s => {
    const existing = s?.name || s?.seller || s?.seller_name;
    if (existing && String(existing).trim()) return s;

    const store = normalizeStoreUrl(s?.store_url || s?.seller_store_url || s?.url);
    const fromStore = store ? nameByStore.get(store) : undefined;
    const resolved = fromStore || defaultName;
    if (!resolved) return s;

    return {
      ...s,
      name: resolved,
      seller: resolved,
      seller_name: resolved,
    };
  });

  return dedupeSellers(enriched);
}

function productListingUrl(listing: any): string {
  return String(listing?.url || listing?.current_url || '').trim();
}

function sellerNameFromListing(listing: any): string | undefined {
  const name = listing?.seller || listing?.seller_name || listing?.name;
  if (typeof name !== 'string') return undefined;
  const trimmed = name.trim();
  return trimmed || undefined;
}

/** Normalize product page URLs for matching seller page source_url. */
export function normalizeProductUrl(input?: string): string {
  if (!input || typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  try {
    const u = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    const path = (u.pathname || '/').replace(/\/+$/, '') || '/';
    return `${u.hostname.toLowerCase()}${path}${u.search}`;
  } catch {
    return trimmed.toLowerCase();
  }
}

function withSellerName(data: any, name: string): any {
  const trimmed = name.trim();
  return { ...data, seller: trimmed, seller_name: trimmed, name: trimmed };
}

/** Collect product-shaped listings from all rows in a scraping task. */
export function collectProductListingsFromTask(
  taskResults: Array<{ listings?: any[]; extracted_data?: any }>
): any[] {
  const out: any[] = [];
  for (const result of taskResults) {
    if (Array.isArray(result.listings)) {
      for (const listing of result.listings) {
        if (listing && listing.is_duplicate !== true) {
          out.push(listing);
        }
      }
    }
    const ed = result.extracted_data;
    if (!ed || typeof ed !== 'object') continue;
    if (Array.isArray(ed)) {
      out.push(...ed.filter(Boolean));
    } else if (Array.isArray(ed.items)) {
      out.push(...ed.items.filter(Boolean));
    } else if (Array.isArray(ed.products)) {
      out.push(...ed.products.filter(Boolean));
    } else if (ed.title || ed.seller || ed.url) {
      out.push(ed);
    }
  }
  return out;
}

/**
 * Seller-page task rows often only have store URL; copy seller name from a product listing in the same task.
 */
export function enrichSellerDataFromTaskProducts(
  data: any,
  taskResults: Array<{ listings?: any[]; extracted_data?: any }>
): any {
  if (!data || typeof data !== 'object') return data;
  if (sellerNameFromListing(data)) return data;

  const listings = collectProductListingsFromTask(taskResults);

  // Seller pages set source_url to the product URL they were discovered from.
  const sourceUrl = data.source_url;
  if (sourceUrl) {
    const srcNorm = normalizeProductUrl(sourceUrl);
    for (const listing of listings) {
      const listingUrl = productListingUrl(listing);
      if (!listingUrl) continue;
      if (normalizeProductUrl(listingUrl) === srcNorm) {
        const name = sellerNameFromListing(listing);
        if (name) return withSellerName(data, name);
      }
    }
  }

  const storeHint = normalizeStoreUrl(
    data.seller_store_url || data.store_url || data.seller_url || data.url
  );
  if (storeHint) {
    for (const listing of listings) {
      const name = sellerNameFromListing(listing);
      if (!name) continue;
      const stores = [
        listing.seller_store_url,
        listing.store_url,
        ...(Array.isArray(listing.seller_urls) ? listing.seller_urls : []),
      ];
      for (const raw of stores) {
        if (raw && normalizeStoreUrl(String(raw)) === storeHint) {
          return withSellerName(data, name);
        }
      }
    }
  }

  return data;
}

export function sellerDisplayName(s: any): string {
  return (
    s?.name ||
    s?.seller ||
    s?.seller_name ||
    s?.identifier ||
    'Unknown seller'
  );
}

export function sellerIdentifier(s: any): string {
  return s?.name || s?.seller || s?.seller_name || s?.identifier || 'Unknown Seller';
}
